import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import {
  FileText, Download, Building2, Users, Wallet, Receipt,
  Calendar, RefreshCw, AlertCircle, CheckCircle, Clock
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function ComplianceReports({ user, onLogout }) {
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [financialYear, setFinancialYear] = useState('2024-2025');
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    fetchEmployees();
    fetchSummary();
  }, [month, year]);

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchSummary = async () => {
    setDataLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/compliance/summary?month=${month}&year=${year}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    } finally {
      setDataLoading(false);
    }
  };

  const handleDownloadPFECR = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/compliance/pf-ecr?month=${month}&year=${year}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PF_ECR_${month}_${year}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      alert('PF ECR file downloaded successfully!');
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadESI = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/compliance/esi-return?month=${month}&year=${year}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ESI_Return_${month}_${year}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      alert('ESI Return file downloaded successfully!');
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadForm16 = async () => {
    if (!selectedEmployee) {
      alert('Please select an employee');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/compliance/form16/${selectedEmployee}?financial_year=${financialYear}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      const form16Data = response.data;
      const content = `
FORM 16 - PART B
${'='.repeat(60)}

Financial Year: ${form16Data.financial_year}

Employee Details:
Name: ${form16Data.employee_name}
PAN: ${form16Data.pan}
Designation: ${form16Data.designation}

${'='.repeat(60)}
INCOME DETAILS
${'='.repeat(60)}

Gross Total Income: Rs.${form16Data.gross_total_income}

Deductions under Chapter VI-A:
Section 80C (PF): Rs.${form16Data.deductions['80C']}
Total Deductions: Rs.${form16Data.deductions.total_deductions}

Taxable Income: Rs.${form16Data.taxable_income}

Tax Deducted at Source: Rs.${form16Data.tax_deducted}

${'='.repeat(60)}
MONTH-WISE BREAKDOWN
${'='.repeat(60)}

${form16Data.months_data.map(m => 
  `${getMonthName(m.month)} ${m.year}: Gross=Rs.${m.gross_salary}, TDS=Rs.${m.deductions.tds}`
).join('\n')}
      `;
      
      const blob = new Blob([content], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Form16_${form16Data.employee_name}_${financialYear}.txt`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      alert('Form 16 downloaded successfully!');
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const getMonthName = (monthNum) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[monthNum - 1];
  };

  // Stat Card Component (Zobox style)
  const StatCard = ({ title, value, subtitle, icon: Icon, color }) => (
    <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
      <div className="flex items-center gap-4">
        <div className="p-3 rounded-xl" style={{ backgroundColor: `${color}15` }}>
          <Icon className="w-6 h-6" style={{ color }} />
        </div>
        <div className="flex-1">
          <p className="text-sm text-gray-500">{title}</p>
          <h3 className="text-2xl font-bold" style={{ color: '#1a1f3a' }}>{value}</h3>
          {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
        </div>
      </div>
    </div>
  );

  if (dataLoading && !summary) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-screen bg-gray-50">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#2b3380' }}></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: '#1a1f3a' }}>Compliance Reports</h1>
            <p className="text-gray-500">Generate PF, ESI, and Tax compliance documents</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={fetchSummary}
              disabled={dataLoading}
              className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${dataLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Period Selection Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg" style={{ backgroundColor: '#2b338015' }}>
              <Calendar className="w-5 h-5" style={{ color: '#2b3380' }} />
            </div>
            <h2 className="text-lg font-semibold" style={{ color: '#1a1f3a' }}>Select Period</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
              <select
                value={month}
                onChange={(e) => setMonth(parseInt(e.target.value))}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none bg-white"
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>{getMonthName(i + 1)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
              <input
                type="number"
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value))}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none"
              />
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard
              title="Total Employees"
              value={summary.total_employees}
              subtitle="Payroll processed"
              icon={Users}
              color="#2b3380"
            />
            <StatCard
              title="PF Contribution"
              value={`Rs.${(summary.pf?.total || 0).toLocaleString()}`}
              subtitle={`Emp: Rs.${summary.pf?.employee_contribution || 0}`}
              icon={Building2}
              color="#4caf50"
            />
            <StatCard
              title="ESI Contribution"
              value={`Rs.${(summary.esi?.total || 0).toLocaleString()}`}
              subtitle={`${summary.esi?.applicable_count || 0} applicable`}
              icon={Receipt}
              color="#7e57c2"
            />
            <StatCard
              title="TDS Deducted"
              value={`Rs.${(summary.tds?.total_deducted || 0).toLocaleString()}`}
              subtitle="Tax at source"
              icon={Wallet}
              color="#ff9800"
            />
          </div>
        )}

        {/* Compliance Documents */}
        <div className="space-y-4">
          {/* PF ECR Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl" style={{ backgroundColor: '#2b338015' }}>
                  <Building2 className="w-6 h-6" style={{ color: '#2b3380' }} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold" style={{ color: '#1a1f3a' }}>PF ECR (Electronic Challan cum Return)</h3>
                  <p className="text-sm text-gray-500">Download PF contribution file for {getMonthName(month)} {year}</p>
                </div>
              </div>
              <button
                onClick={handleDownloadPFECR}
                disabled={loading}
                className="px-5 py-2.5 text-white rounded-lg flex items-center gap-2 transition-colors"
                style={{ backgroundColor: loading ? '#cccccc' : '#2b3380' }}
              >
                {loading ? (
                  <><Clock className="w-4 h-4 animate-spin" />Generating...</>
                ) : (
                  <><Download className="w-4 h-4" />Download PF ECR</>
                )}
              </button>
            </div>
          </div>

          {/* ESI Return Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl" style={{ backgroundColor: '#7e57c215' }}>
                  <Receipt className="w-6 h-6" style={{ color: '#7e57c2' }} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold" style={{ color: '#1a1f3a' }}>ESI Return</h3>
                  <p className="text-sm text-gray-500">Download ESI contribution file for {getMonthName(month)} {year}</p>
                </div>
              </div>
              <button
                onClick={handleDownloadESI}
                disabled={loading}
                className="px-5 py-2.5 text-white rounded-lg flex items-center gap-2 transition-colors"
                style={{ backgroundColor: loading ? '#cccccc' : '#7e57c2' }}
              >
                {loading ? (
                  <><Clock className="w-4 h-4 animate-spin" />Generating...</>
                ) : (
                  <><Download className="w-4 h-4" />Download ESI Return</>
                )}
              </button>
            </div>
          </div>

          {/* Form 16 Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 rounded-xl" style={{ backgroundColor: '#ff980015' }}>
                <FileText className="w-6 h-6" style={{ color: '#ff9800' }} />
              </div>
              <div>
                <h3 className="text-lg font-semibold" style={{ color: '#1a1f3a' }}>Form 16 (TDS Certificate)</h3>
                <p className="text-sm text-gray-500">Generate TDS certificate for individual employees</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Financial Year</label>
                <input
                  type="text"
                  value={financialYear}
                  onChange={(e) => setFinancialYear(e.target.value)}
                  placeholder="2024-2025"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Employee</label>
                <select
                  value={selectedEmployee}
                  onChange={(e) => setSelectedEmployee(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none bg-white"
                >
                  <option value="">Choose Employee</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={handleDownloadForm16}
                  disabled={loading || !selectedEmployee}
                  className="w-full px-5 py-2.5 text-white rounded-lg flex items-center justify-center gap-2 transition-colors"
                  style={{ backgroundColor: loading || !selectedEmployee ? '#cccccc' : '#ff9800' }}
                >
                  {loading ? (
                    <><Clock className="w-4 h-4 animate-spin" />Generating...</>
                  ) : (
                    <><Download className="w-4 h-4" />Download Form 16</>
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> Form 16 is generated for the complete financial year (April to March). Make sure payroll has been processed for all months of the selected financial year.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default ComplianceReports;
